import type { MaterialListItem } from "@edu/shared";
import { externalCallHost } from "@edu/shared";

/**
 * Слова экрана «Ссылки и файлы» у ученика (`docs/material-receipt-design.md`,
 * разделы 7, 8, 14 и 15).
 *
 * Читает их ребёнок, а рядом может стоять родитель. Отдельным модулем с
 * тестами, а не ветками в разметке, по той же причине, что у преподавательского
 * соседа `features/students/material-text.ts`: правило проще всего нарушить
 * незаметно. Соседа отсюда НЕ импортируем: экран ученика не тянет модули
 * преподавателя, а общее у них одно, `externalCallHost` из общего пакета.
 */

/**
 * Честная строка раздела 15: открытие видно преподавателю.
 *
 * ⚠️ **Скрывать её нельзя, и она стоит на экране всегда, а не условно.** Отметка
 * о действии ребёнка, показанная взрослому и не объявленная ребёнку, называется
 * иначе, чем квитанция. Вторая фраза о вехе обязательна по той же причине: у
 * видео и аудио преподаватель видит больше, чем «открыт».
 *
 * ⚠️ Слова сильнее «открыли» здесь нет намеренно (раздел 6.2 и 15): ученик,
 * знающий про отметку, может её сымитировать, поэтому обещать, что
 * преподаватель видит «посмотрели», значило бы соврать по построению.
 */
export const RECEIPT_DISCLOSURE =
  "Преподаватель видит, когда вы впервые открыли ссылку или файл. У видео и аудио видно ещё, примерно до какого места вы дошли.";

/** Пустой список. Про список, а не про человека: «вам ничего не прислали» звучит упрёком. */
export const EMPTY_TITLE = "Ссылок и файлов пока нет";
export const EMPTY_TEXT = "Здесь появятся ссылки и файлы, которые пришлёт преподаватель.";

/**
 * Куда ведёт ссылка, открытым текстом рядом с кнопкой.
 *
 * ⚠️ **Домен показывается всегда** (раздел 8 проекта): адрес ввёл один
 * человек, а нажимает другой, часто несовершеннолетний. Функция та же, что у
 * ссылки на созвон, а не своя. `null` значит «адрес не разбирается», и тогда
 * кнопки нет вовсе: вести человека по адресу, которого мы сами не можем
 * назвать, нельзя.
 */
export function linkHostText(url: string | null): string | null {
  const host = externalCallHost(url);
  return host === null ? null : `откроется сайт ${host}`;
}

/** Что сказать вместо кнопки, когда открыть нечего. */
export const BROKEN_LINK_TEXT = "Ссылка с ошибкой, открыть её не получится";

/**
 * Файл стёрт, а строка материала осталась надгробием (раздел 5 проекта).
 * Слово «удалён» и кем, как у медиа со слайдов: иначе ребёнок решит, что
 * сломалось у него.
 */
export const FILE_GONE_TEXT = "Файл удалён преподавателем";

/**
 * Вторая строка под названием: от кого и что это.
 *
 * Имя преподавателя стоит первым: у ученика их бывает несколько, и «от кого»
 * отвечает на вопрос раньше, чем «что». Пустое имя (вкладка старее выкатки)
 * просто не печатается.
 */
export function mySourceText(
  item: Pick<MaterialListItem, "kind" | "fileId" | "fileKind" | "teacherName">,
): string {
  // У стёртого файла слово «файл» без вида: что его больше нет, говорит ячейка
  // кнопки, и второй раз в той же строке это не нужно
  const what = item.kind === "link" ? "ссылка" : item.fileId === null ? "файл" : fileWord(item.fileKind);
  const who = item.teacherName.trim();
  return who === "" ? what : `${who} · ${what}`;
}

function fileWord(kind: MaterialListItem["fileKind"]): string {
  switch (kind) {
    case "video":
      return "видео";
    case "audio":
      return "аудио";
    case "image":
      return "картинка";
    case "doc":
      return "документ";
    default:
      return "файл";
  }
}
